/** Backend module keys — must match GET /admin/me/permissions. */
export const RBAC_MODULES = [
  "user",
  "rating",
  "roles",
  "permission",
  "cart",
  "order",
  "support",
  "product",
  "category",
  "sale",
  "store",
  "staff",
  "refund",
  "daily_entry",
  "chat",
  "dashboard",
  "analytics_dashboard",
  "activity_log",
  "price_history",
  "payroll",
];

/**
 * Numeric `modules.id` from backend seed order (CORE_MODULE_NAMES + EXTRA_MODULE_NAMES).
 * If your DB diverges, fix this map or rely on API fields on each permission row (moduleId).
 */
export const RBAC_MODULE_DB_ID = {
  user: 1,
  rating: 2,
  roles: 3,
  permission: 4,
  cart: 5,
  order: 6,
  support: 7,
  product: 8,
  category: 9,
  sale: 10,
  store: 11,
  staff: 12,
  refund: 13,
  daily_entry: 14,
  chat: 15,
  dashboard: 16,
  /** Align with backend EXTRA_MODULE_NAMES order when possible. */
  activity_log: 17,
  price_history: 18,
  analytics_dashboard: 19,
  payroll: 20,
};

export function getRbacModuleDbId(moduleKey) {
  return RBAC_MODULE_DB_ID[moduleKey] ?? null;
}

/** Non-null permissions object from GET /admin/me/permissions (all roles, including Super Admin). */
function permissionsMatrix(permState) {
  const m = permState?.permissions;
  if (m && typeof m === "object" && !Array.isArray(m) && Object.keys(m).length > 0) {
    return m;
  }
  return null;
}

/**
 * Matrix row for a module. If the backend adds a new module before this client’s cached
 * permissions include it, `row` is missing: only Super Admin is treated as allowed so
 * the sidebar and routes keep working until the next permissions fetch / template backfill.
 */
function matrixFlag(permState, matrix, moduleKey, flag) {
  const row = matrix[moduleKey];
  if (row == null) return Boolean(permState?.isSuperAdmin);
  // Support both camelCase and snake_case permission flags from backend.
  // Examples: statusUpdate vs status_update, can_read vs read.
  const direct = row?.[flag];
  if (direct === 1) return true;
  if (direct === 0) return false;

  const snake = String(flag)
    .replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)
    .replace(/^_/, "");
  const alt = row?.[snake];
  return alt === 1;
}

export function canReadModule(permState, moduleKey) {
  if (!moduleKey) return true;
  if (permState?.fruitMode) return true;
  const matrix = permissionsMatrix(permState);
  if (matrix) {
    return matrixFlag(permState, matrix, moduleKey, "read");
  }
  if (permState?.isSuperAdmin) return true;
  return false;
}

export function canCreateModule(permState, moduleKey) {
  if (!moduleKey) return true;
  if (permState?.fruitMode) return true;
  const matrix = permissionsMatrix(permState);
  if (matrix) {
    return matrixFlag(permState, matrix, moduleKey, "create");
  }
  if (permState?.isSuperAdmin) return true;
  return false;
}

export function canUpdateModule(permState, moduleKey) {
  if (!moduleKey) return true;
  if (permState?.fruitMode) return true;
  const matrix = permissionsMatrix(permState);
  if (matrix) {
    return matrixFlag(permState, matrix, moduleKey, "update");
  }
  if (permState?.isSuperAdmin) return true;
  return false;
}

export function canDeleteModule(permState, moduleKey) {
  if (!moduleKey) return true;
  if (permState?.fruitMode) return true;
  const matrix = permissionsMatrix(permState);
  if (matrix) {
    return matrixFlag(permState, matrix, moduleKey, "delete");
  }
  if (permState?.isSuperAdmin) return true;
  return false;
}

export function canStatusUpdateModule(permState, moduleKey) {
  if (!moduleKey) return true;
  if (permState?.fruitMode) return true;
  const matrix = permissionsMatrix(permState);
  if (matrix) {
    return matrixFlag(permState, matrix, moduleKey, "statusUpdate");
  }
  if (permState?.isSuperAdmin) return true;
  return false;
}

/**
 * Soft-delete / adjust order lines (B2B order correction). Uses matrix `order.adjust` when the
 * backend sends it; otherwise falls back to delete or update so existing roles keep working.
 */
export function canAdjustOrderLines(permState) {
  if (permState?.fruitMode) return true;
  const matrix = permissionsMatrix(permState);
  if (matrix?.order?.adjust === 1) return true;
  return canDeleteModule(permState, "order") || canUpdateModule(permState, "order");
}

/** Generic check: action ∈ read | create | update | delete | statusUpdate | adjust */
export function can(permState, moduleKey, action) {
  switch (action) {
    case "read":
      return canReadModule(permState, moduleKey);
    case "create":
      return canCreateModule(permState, moduleKey);
    case "update":
      return canUpdateModule(permState, moduleKey);
    case "delete":
      return canDeleteModule(permState, moduleKey);
    case "statusUpdate":
      return canStatusUpdateModule(permState, moduleKey);
    case "adjust":
      return moduleKey === "order" ? canAdjustOrderLines(permState) : false;
    default:
      return false;
  }
}
